import type {
  SalespointCreateData,
  SalespointUpdateData,
  SalespointResponse,
  SalespointCreateResponse,
  SalespointUpdateResponse,
  SalespointOption
} from '../salespoint.types'
import { doGet, doGetOld, doPostOld, doPatchOld } from '@/services/api/base'
import { pathWithPageAndFilters } from '@/services/api/base'

/**
 * Get all salespoints with pagination and filtering support
 * @param page - The page number to fetch (default: 1)
 * @param filters - Filters to apply to the salespoints (default: [])
 * @param pageSize - Number of items per page (default: 10)
 * @returns Promise<ApiPaginatedResponse<Salespoint>> Paginated list of salespoints
 */
export async function getSalespoints(
  page: number = 1,
  filters: ApiFilter[] = [],
  pageSize: number = 10
): Promise<ApiListResponseOld<SalespointResponse>> {
  return await doGetOld(pathWithPageAndFilters('/api/users/salespoints', filters, page, pageSize))
}

export const getSalespointsOptions = async (): Promise<ApiResponse<{ salespoints: SalespointOption[] }>> => {
  return await doGet(`/api/users/salespoints/all`)
}

/**
 * Get a salespoint by ID
 * @param id - The salespoint ID to find
 * @returns Promise<Salespoint | undefined> The found salespoint or undefined
 */
export async function getSalespointById(id: string): Promise<SalespointResponse | undefined> {
  return await doGetOld(`/api/users/salespoints/${id}`)
}

/**
 * Create a new salespoint
 * @param data - The salespoint data to create
 * @returns Promise<Salespoint> The created salespoint
 */
export async function createSalespoint(data: SalespointCreateData): Promise<SalespointCreateResponse> {
  const attrs = {
    name: data.name,
    location_id: data.locationId,
    printer_id: data.printerId,
    payment_terminal_id: data.paymentTerminalId
  }
  return await doPostOld('/api/users/salespoints', attrs)
}

/**
 * Update a salespoint
 * @param id - The salespoint ID to update
 * @param data - The salespoint data to update
 * @returns Promise<SalespointUpdateResponse> The updated salespoint
 */
export async function updateSalespoint(id: string, data: SalespointUpdateData): Promise<SalespointUpdateResponse> {
  const attrs = {
    name: data.name,
    location_id: data.locationId,
    printer_id: data.printerId,
    payment_terminal_id: data.paymentTerminalId
  }
  return await doPatchOld(`/api/users/salespoints/${id}`, attrs)
}

/**
 * Delete a salespoint
 * @param id - The salespoint ID to delete
 * @returns Promise<ApiNoContentResponse> The deleted salespoint
 */
export async function deleteSalespoint(id: string): Promise<ApiNoContentResponse> {
  return await doPostOld(`/api/users/salespoints/${id}/archive`, {})
}

/**
 * Deactivate a salespoint
 * @param id - The salespoint ID to deactivate
 * @returns Promise<ApiNoContentResponse> The deactivated salespoint
 */
export async function deactivateSalespoint(id: string): Promise<ApiNoContentResponse> {
  return await doPostOld(`/api/users/salespoints/${id}/deactivate`, {})
}

/**
 * Activate a salespoint
 * @param id - The salespoint ID to activate
 * @returns Promise<ApiNoContentResponse> The activated salespoint
 */
export async function activateSalespoint(id: string): Promise<ApiNoContentResponse> {
  return await doPostOld(`/api/users/salespoints/${id}/activate`, {})
}

export const salespointApi = {
  getSalespoints,
  createSalespoint,
  updateSalespoint,
  getSalespointById,
  deleteSalespoint,
  deactivateSalespoint,
  activateSalespoint,
  getSalespointsOptions
}
